/**
 * useViewTemplate — DSL 视图模板 Hook
 *
 * 核心思路:
 *   1. per-screen override
 *   2. classic mode registered template
 *   3. tenant API default template
 *   4. non-classic registered / specialized domain template
 *   5. generic fallback from EntityMeta.fields
 *
 * 这样前端所有组件统一消费 DSL, 无论模板是数据库配置还是自动生成
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import i18next from 'i18next';
import type { EntityMeta, FieldDef } from '@/types/entity';
import { getSpecializedFallbackTemplate, getClassicTemplate, getRegisteredTemplate, getRegisteredTemplates } from '@/lib/fallback-view-templates';
import { useAppStore } from '@/stores/app-store';

// ─── DSL Types (mirrors backend) ───────────────────

export type ViewType = 'list' | 'detail' | 'card' | 'form';

export interface ListColumnDSL {
  field: string;
  label?: string;
  width?: number;
  minWidth?: number;
  sortable?: boolean;
  filterable?: boolean;
  format?: 'date' | 'datetime' | 'currency' | 'number' | 'status' | 'link';
  hidden?: boolean;
  // === Spreadsheet Grid 扩展 ===
  /** 是否允许 inline edit（默认 false, undefined 时 fallback 到 FieldDef.updatable） */
  editable?: boolean;
  /** 角色白名单 — 只有列表中的角色可编辑此字段; 其他角色看到 🔒 */
  editableBy?: string[];
  /** 列固定方向 */
  pinned?: 'left' | 'right';
  // === 3-line mode 扩展 ===
  /** 3-line mode: secondary text shown below primary */
  secondaryField?: string;
  /** 3-line mode: tertiary text shown smallest */
  tertiaryField?: string;
  /** 3-line mode: badge field rendered as chip */
  badgeField?: string;
}

export interface ListFilterDSL {
  field: string;
  label?: string;
  type?: 'text' | 'select' | 'date' | 'datetime' | 'boolean';
  options?: Array<{
    label: string;
    value: string;
  }>;
  placeholder?: string;
}

export interface DetailSectionDSL {
  title: string;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  fields: Array<{
    field: string;
    label?: string;
    span?: number;
    format?: string;
    /**
     * 控制该字段在 section 内联编辑模式下是否可编辑:
     *   false  → 即使 FieldDef.updatable = true 也不展示编辑器
     *   true   → 强制可编辑 (仍需 FieldDef.updatable = true)
     *   absent → 默认由 FieldDef.updatable 决定
     */
    editable?: boolean;
  }>;
}

export interface FormFieldDSL {
  field: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'toggle' | 'hidden'
    | 'photo' | 'signature' | 'location' | 'card-select' | 'lookup';
  options?: Array<{
    label: string;
    value: string;
    description?: string;
    icon?: string;
    color?: string;
  }>;
  span?: number;
  group?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  renderAs?: 'card-grid' | 'card-list' | 'default';
  maxItems?: number;
  captureMode?: 'camera' | 'file' | 'both';
  visibleWhen?: {
    field: string;
    equals?: unknown;
    notEquals?: unknown;
  };
}

export interface CardDSL {
  titleField: string;
  subtitleField?: string;
  badgeField?: string;
  badgeFormat?: 'status' | 'text';
  bodyFields?: string[];
  actionFields?: string[];
  avatarField?: string;
  timestampField?: string;
  colorField?: string;
}

export interface ThreeLineElement {
  field: string;
  format?: string;
  /** Render as a status-like badge chip */
  badge?: boolean;
  /** Use muted/lighter text */
  muted?: boolean;
}

/**
 * ThreeLineRowDSL — 3-line card layout for list rows.
 * Each line is an array of elements (typically 1, sometimes 2).
 */
export interface ThreeLineRowDSL {
  line1: ThreeLineElement[];
  line2: ThreeLineElement[];
  line3: ThreeLineElement[];
}

/**
 * ColumnGroupDSL — meaningful column segmentation for list views.
 *
 * 1-line mode: renders as multi-level header (group label spans above column headers).
 * 3-line mode: horizontal blocks within each card row.
 */
export interface ColumnGroupDSL {
  key: string;
  label: string;
  columns: string[];
  threeLineLayout?: {
    line1: ThreeLineElement[];
    line2?: ThreeLineElement[];
    line3?: ThreeLineElement[];
  };
}

export interface WizardStepDSL {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  fields: FormFieldDSL[];
  optional?: boolean;
  autoAdvance?: boolean;
  skipLabel?: string;
  nextLabel?: string;
}

export interface WizardDSL {
  steps: WizardStepDSL[];
  submitLabel?: string;
  submitAction?: string;
  showStepIndicator?: boolean;
}

export interface StatusActionDSL {
  status: string;
  nextStatus: string;
  label: string;
  color?: string;
  icon?: string;
}

export interface PageBannerDSL {
  tone?: 'info' | 'warning';
  text: string;
}

export interface PageActionDSL {
  id: string;
  label: string;
  icon?: 'send' | 'mail' | 'file';
  tone?: 'default' | 'outline';
}

export interface RelatedListDSL {
  title: string;
  slug: string;
  resourcePath: string;
  queryParam: string;
  sourceField: string;
  emptyMessage?: string;
}

export interface PageMetaDSL {
  title?: string;
  subtitle?: string;
  searchPlaceholder?: string;
  banners?: PageBannerDSL[];
  actions?: PageActionDSL[];
  relatedLists?: RelatedListDSL[];
}

export interface ViewLayoutDSL {
  desktopColumns?: 1 | 2 | 3 | 4 | 5 | 6;
  mobileColumns?: 1 | 2;
  pageMode?: 'record' | 'planner' | 'workspace' | 'major-list';
  surface?: 'desktop-first' | 'balanced' | 'compact-first';
}

export interface MajorListConfigDSL {
  kind?: 'grid' | 'major-grid' | 'planner-like-grid';
  lineMode?: '1-line' | '3-line' | '1|3';
  sorting?: 'server' | 'client';
  paging?: 'server' | 'client';
  search?: {
    enabled?: boolean;
    placeholder?: string;
  };
  topFilters?: Array<{
    key: string;
    label?: string;
    type?: 'text' | 'select' | 'multi-select' | 'date' | 'date-range' | 'boolean';
  }>;
  columnSets?: Array<{
    id: string;
    label: string;
    description?: string;
    visibleColumns?: string[];
  }>;
  savedViews?: boolean;
  rowActions?: string[];
  /** Column grouping — segments columns into meaningful blocks */
  columnGroups?: ColumnGroupDSL[];
}

export interface PanelContentDSL {
  type: 'grid' | 'detail' | 'timeline' | 'documents' | 'ai' | 'map' | 'exceptions' | 'custom';
  title?: string;
  entitySlug?: string;
  viewType?: ViewType;
  rendererKey?: string;
  resourcePath?: string;
}

export interface MajorPanelsDSL {
  main?: {
    type?: 'grid' | 'planner-grid' | 'workspace-grid';
    frozenColumns?: number;
    allowInlineEdit?: boolean;
  };
  assist?: {
    enabled: boolean;
    side?: 'right';
    mode?: 'slide' | 'sliding-resizable' | 'dock-sheet';
    resizable?: boolean;
    collapsible?: boolean;
    defaultOpen?: boolean;
    width?: 'sm' | 'md' | 'lg' | 'xl' | number;
    content?: PanelContentDSL;
    allowedContent?: PanelContentDSL['type'][];
  };
}

export interface DetailDockDSL {
  enabled: boolean;
  side?: 'right';
  mode?: 'dock-sheet' | 'drawer';
  width?: 'md' | 'lg' | 'xl' | number;
  openFrom?: Array<'row-click' | 'row-double-click' | 'action-button' | 'bulk-action'>;
  defaultViewMode?: 'view' | 'edit';
  allowEdit?: boolean;
  headerSummary?: Array<{
    field: string;
    label?: string;
    format?: string;
  }>;
  form?: {
    desktopColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    mobileColumns?: 1 | 2;
    fields?: FormFieldDSL[];
    sections?: Array<{
      id?: string;
      title?: string;
      columns?: 1 | 2 | 3 | 4 | 5 | 6;
      fields: FormFieldDSL[];
    }>;
  };
  subLists?: Array<{
    id: string;
    label: string;
    slug?: string;
    resourcePath?: string;
    queryParam?: string;
    sourceField?: string;
    editable?: 'none' | 'row' | 'cell';
    actions?: Array<'add' | 'edit' | 'delete' | 'copy' | 'void' | 'reorder' | 'open'>;
    columns?: ListColumnDSL[];
    fields?: FormFieldDSL[];
  }>;
}

export interface ViewTemplateDSL {
  columns?: ListColumnDSL[];
  filters?: ListFilterDSL[];
  sections?: DetailSectionDSL[];
  fields?: FormFieldDSL[];
  card?: CardDSL;
  wizard?: WizardDSL;
  statusActions?: StatusActionDSL[];
  density?: 'compact' | 'default' | 'comfortable';
  presets?: Array<{ key: string; label: string; visibleColumns: string[] }>;
  page?: PageMetaDSL;
  layout?: ViewLayoutDSL;
  list?: MajorListConfigDSL;
  panels?: MajorPanelsDSL;
  detailDock?: DetailDockDSL;
}

interface ViewTemplateRecord {
  id: string;
  entity_slug: string;
  view_type: string;
  name: string;
  is_default: boolean;
  template: ViewTemplateDSL;
}

// ─── Hook ──────────────────────────────────────────

interface UseViewTemplateResult {
  template: ViewTemplateDSL | null;
  loading: boolean;
  source?: 'screen_template' | 'classic_template' | 'tenant_view_template' | 'domain_template_library' | 'generic_meta_fallback' | 'none';
  templateId?: string;
}

export function useViewTemplate(
  slug: string | undefined,
  viewType: ViewType,
  meta: EntityMeta | null,
): UseViewTemplateResult {
  const [template, setTemplate] = useState<ViewTemplateDSL | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<UseViewTemplateResult['source']>('none');
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const { designMode, screenTemplates } = useAppStore();

  useEffect(() => {
    if (!slug || !meta) {
      setLoading(false);
      setSource('none');
      setTemplateId(undefined);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);

      // ── 1. Per-screen override (highest priority) ───────────────────────
      // User explicitly chose a template for this specific screen.
      // Takes priority over both global designMode and API defaults.
      const perScreenId = screenTemplates[`${slug}:${viewType}`];
      if (perScreenId) {
        const perScreenTpl = getRegisteredTemplate(slug!, viewType, perScreenId);
        if (perScreenTpl && !cancelled) {
          setTemplate(perScreenTpl);
          setSource('screen_template');
          setTemplateId(perScreenId);
          setLoading(false);
          return;
        }
      }

      // ── 2. Classic mode short-circuit ───────────────────────────────────
      // When the user has switched to Classic layout globally, bypass the API
      // and serve a pre-built compact DSL synchronously.
      if (designMode === 'classic') {
        const classicTpl = getClassicTemplate(slug!, viewType);
        if (classicTpl && !cancelled) {
          setTemplate(classicTpl);
          setSource('classic_template');
          setTemplateId('classic');
          setLoading(false);
          return;
        }
        // No classic variant defined for this slug → fall through to normal path.
      }

      try {
        // Try to fetch default template from API
        const res = await api.get<{ items: ViewTemplateRecord[] }>(
          `/view-templates?entitySlug=${slug}&viewType=${viewType}`,
        );
        const defaultTpl = res.items?.find((t) => t.is_default);
        if (!cancelled && defaultTpl?.template && meta) {
          setTemplate(mergeTemplateWithFallback(defaultTpl.template, meta, viewType));
          setSource('tenant_view_template');
          setTemplateId(defaultTpl.id);
          setLoading(false);
          return;
        }
      } catch {
        // API might not have templates yet — fall through to auto-generate
      }

      const entityMeta = meta!;
      const registeredDomainTpl = getRegisteredTemplates(slug!, viewType).find((entry) => entry.id !== 'classic');
      if (!cancelled && registeredDomainTpl) {
        setTemplate(mergeTemplateWithFallback(registeredDomainTpl.template, entityMeta, viewType));
        setSource('domain_template_library');
        setTemplateId(registeredDomainTpl.id);
        setLoading(false);
        return;
      }

      const specializedFallback = getSpecializedFallbackTemplate(entityMeta, viewType, slug);
      if (!cancelled && specializedFallback) {
        setTemplate(specializedFallback);
        setSource('domain_template_library');
        setTemplateId(`${slug}:${viewType}:specialized`);
        setLoading(false);
        return;
      }

      // Auto-generate generic DSL from EntityMeta
      if (!cancelled && meta) {
        const generated = generateGenericFallbackDSL(meta, viewType);
        setTemplate(generated);
        setSource('generic_meta_fallback');
        setTemplateId(undefined);
      }
      if (!cancelled) setLoading(false);
    }

    void load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, viewType, meta, designMode, screenTemplates]);

  return { template, loading, source, templateId };
}

// ─── Fallback DSL Generator ────────────────────────

function generateFallbackDSL(meta: EntityMeta, viewType: ViewType, slug?: string): ViewTemplateDSL {
  const specialized = getSpecializedFallbackTemplate(meta, viewType, slug);
  if (specialized) return specialized;

  return generateGenericFallbackDSL(meta, viewType);
}

function generateGenericFallbackDSL(meta: EntityMeta, viewType: ViewType): ViewTemplateDSL {
  switch (viewType) {
    case 'list':
      return generateListDSL(meta);
    case 'detail':
      return generateDetailDSL(meta);
    case 'form':
      return generateFormDSL(meta);
    case 'card':
      return generateCardDSL(meta);
    default:
      return {};
  }
}

// ─── List DSL Generator ────────────────────────────

function generateListDSL(meta: EntityMeta): ViewTemplateDSL {
  const skip = new Set(['id', 'tenantId', 'data', 'createdAt', 'updatedAt', 'deletedAt']);
  // Also exclude FK reference columns (e.g. customerId, job_id) — raw GUIDs are not useful in lists
  const fields = meta.fields.filter(
    (f) => !skip.has(f.name) && !/Id$|_id$/.test(f.name),
  );

  // Priority order: status fields first, then name/number fields, then others
  const prioritized = sortFieldsByPriority(fields);

  // Take max 8 columns
  const columns: ListColumnDSL[] = prioritized.slice(0, 8).map((f, i) => ({
    field: f.name,
    label: f.label ?? f.name,
    sortable: f.filterable || i === 0,
    filterable: f.filterable,
    format: inferFormat(f),
    hidden: false,
  }));

  const filters: ListFilterDSL[] = meta.fields
    .filter((field) =>
      field.filterable &&
      field.name !== 'status' &&
      !/Id$|_id$/.test(field.name),
    )
    .slice(0, 4)
    .map((field) => ({
      field: field.name,
      label: field.label ?? field.name,
      type: inferListFilterType(field),
      options: field.enumValues?.map((value) => ({ label: value, value })),
      placeholder: field.label ?? field.name,
    }));

  return { columns, filters };
}

// ─── Detail DSL Generator ──────────────────────────

function generateDetailDSL(meta: EntityMeta): ViewTemplateDSL {
  const skip = new Set(['tenantId', 'data', 'id']);
  const fields = meta.fields.filter((f) => !skip.has(f.name));

  // Group fields into sections by heuristic
  const sections: DetailSectionDSL[] = [];

  // Section 1: Key info (numbers, status, type — NOT raw id GUIDs)
  const keyFields = fields.filter((f) =>
    f.name.includes('Number') || f.name.includes('number') ||
    f.name.includes('Name') || f.name.includes('name') ||
    f.name.includes('Code') || f.name.includes('code') ||
    f.type === 'enum' ||
    f.name === 'status',
  );
  if (keyFields.length > 0) {
    sections.push({
      title: i18next.t('viewTemplate.basicInfo'),
      columns: 6,
      fields: keyFields.map((f) => ({
        field: f.name,
        label: f.label,
        span: inferDetailSpan(f),
        format: inferFormat(f)?.toString(),
      })),
    });
  }

  // Section 2: Amounts / Finance
  const financeFields = fields.filter((f) =>
    !keyFields.includes(f) && (
      f.name.includes('Amount') || f.name.includes('amount') ||
      f.name.includes('Price') || f.name.includes('price') ||
      f.name.includes('Rate') || f.name.includes('rate') ||
      f.name.includes('Cost') || f.name.includes('cost') ||
      f.name.includes('currency') || f.name.includes('Currency') ||
      f.name.includes('Limit') || f.name.includes('limit') ||
      f.name.includes('Credit') || f.name.includes('credit') ||
      f.name.includes('exchange') || f.name.includes('Exchange')
    ),
  );
  if (financeFields.length > 0) {
    sections.push({
      title: i18next.t('viewTemplate.financialInfo'),
      columns: 6,
      fields: financeFields.map((f) => ({
        field: f.name,
        label: f.label,
        span: inferDetailSpan(f),
        format: inferFormat(f)?.toString(),
      })),
    });
  }

  // Section 3: Dates / Timestamps
  const dateFields = fields.filter((f) =>
    !keyFields.includes(f) && !financeFields.includes(f) && (
      f.type === 'datetime' || f.type === 'date'
    ),
  );
  if (dateFields.length > 0) {
    sections.push({
      title: i18next.t('viewTemplate.timeline'),
      columns: 6,
      fields: dateFields.map((f) => ({
        field: f.name,
        label: f.label,
        span: inferDetailSpan(f),
        format: f.type,
      })),
    });
  }

  // Section 4: Remaining fields (exclude FK GUID references)
  const used = new Set([...keyFields, ...financeFields, ...dateFields].map((f) => f.name));
  const rest = fields.filter((f) => !used.has(f.name) && !(/Id$/.test(f.name) || /_id$/.test(f.name)));
  if (rest.length > 0) {
    sections.push({
      title: i18next.t('viewTemplate.otherInfo'),
      columns: 6,
      fields: rest.map((f) => ({
        field: f.name,
        label: f.label,
        span: inferDetailSpan(f),
        format: inferFormat(f)?.toString(),
      })),
    });
  }

  return { sections };
}

// ─── Form DSL Generator ────────────────────────────

function generateFormDSL(meta: EntityMeta): ViewTemplateDSL {
  const skip = new Set(['id', 'tenantId', 'createdAt', 'updatedAt']);
  const fields = meta.fields.filter((f) => !skip.has(f.name));

  const formFields: FormFieldDSL[] = fields.map((f) => ({
    field: f.name,
    label: f.label ?? f.name,
    required: f.required,
    type: inferFormFieldType(f),
    options:
      f.enumValues?.map((v) => ({ label: v, value: v })),
    span: inferFormSpan(f),
    validation: inferValidation(f),
  }));

  return { fields: formFields };
}

// ─── Card DSL Generator ────────────────────────────

function generateCardDSL(meta: EntityMeta): ViewTemplateDSL {
  const fields = meta.fields;
  const titleField = fields.find((f) =>
    f.name.includes('Number') || f.name.includes('number') ||
    f.name.includes('Name') || f.name.includes('name'),
  ) ?? fields.find((f) => f.name === 'id');

  const badgeField = fields.find((f) => f.name === 'status' || f.type === 'enum');
  const subtitleField = fields.find((f) =>
    f !== titleField && (f.name.includes('type') || f.name.includes('Type')),
  );

  return {
    card: {
      titleField: titleField?.name ?? 'id',
      subtitleField: subtitleField?.name,
      badgeField: badgeField?.name,
      bodyFields: fields
        .filter((f) => f !== titleField && f !== badgeField && f !== subtitleField)
        .slice(0, 3)
        .map((f) => f.name),
    },
  };
}

// ─── Helpers ───────────────────────────────────────

function sortFieldsByPriority(fields: FieldDef[]): FieldDef[] {
  const priority = (f: FieldDef): number => {
    // 'id' and FK fields are pre-filtered in generateListDSL; assign low priority as safety net
    if (f.name === 'id') return 99;
    if (/Id$|_id$/.test(f.name)) return 98;
    if (f.name.includes('Number') || f.name.includes('number')) return 1;
    if (f.name.includes('Name') || f.name.includes('name')) return 2;
    if (f.name === 'status') return 3;
    if (f.type === 'enum') return 4;
    if (f.name.includes('Amount') || f.name.includes('amount')) return 5;
    if (f.type === 'datetime' || f.type === 'date') return 9;
    if (f.name.includes('Id') || f.name.includes('_id')) return 8;
    return 6;
  };
  return [...fields].sort((a, b) => priority(a) - priority(b));
}

function mergeTemplateWithFallback(
  template: ViewTemplateDSL,
  meta: EntityMeta,
  viewType: ViewType,
): ViewTemplateDSL {
  const fallback = generateFallbackDSL(meta, viewType);
  return {
    ...fallback,
    ...template,
    columns: template.columns ?? fallback.columns,
    filters: template.filters ?? fallback.filters,
    sections: template.sections ?? fallback.sections,
    fields: template.fields ?? fallback.fields,
    card: template.card ?? fallback.card,
    wizard: template.wizard ?? fallback.wizard,
    statusActions: template.statusActions ?? fallback.statusActions,
    presets: template.presets ?? fallback.presets,
    density: template.density ?? fallback.density,
    page: template.page ?? fallback.page,
  };
}

function inferFormat(f: FieldDef): ListColumnDSL['format'] | undefined {
  if (f.name === 'status' || (f.type === 'enum' && f.enumValues)) return 'status';
  if (f.type === 'number' && (f.name.includes('Amount') || f.name.includes('amount') || f.name.includes('Price') || f.name.includes('price') || f.name.includes('Cost') || f.name.includes('credit'))) return 'currency';
  if (f.type === 'number') return 'number';
  if (f.type === 'date') return 'date';
  if (f.type === 'datetime') return 'datetime';
  return undefined;
}

function inferListFilterType(f: FieldDef): ListFilterDSL['type'] {
  if (f.enumValues && f.enumValues.length > 0) return 'select';
  if (f.type === 'boolean') return 'boolean';
  if (f.type === 'datetime') return 'datetime';
  if (f.type === 'date') return 'date';
  return 'text';
}

function inferFormFieldType(f: FieldDef): FormFieldDSL['type'] {
  if (f.enumValues && f.enumValues.length > 0) return 'select';
  if (f.type === 'uuid' && f.refSlug) return 'lookup';
  if (f.type === 'number') return 'number';
  if (f.type === 'date') return 'date';
  if (f.type === 'datetime') return 'date';
  if (f.type === 'boolean') return 'toggle';
  if (f.type === 'json') return 'textarea';
  if (f.name === 'data') return 'textarea';
  return 'text';
}

function inferValidation(f: FieldDef): FormFieldDSL['validation'] | undefined {
  if (f.type === 'number') {
    return { min: 0 };
  }
  return undefined;
}

function inferDetailSpan(f: FieldDef): number {
  if (f.type === 'json' || f.type === 'text') return 2;
  if (/description|address|remark|remarks|note|notes|instruction|instructions/i.test(f.name)) return 2;
  return 1;
}

function inferFormSpan(f: FieldDef): number {
  if (f.type === 'json' || f.type === 'text') return 2;
  if (/description|address|remark|remarks|note|notes|instruction|instructions/i.test(f.name)) return 2;
  return 1;
}
